import axiosClient from "./axios-client/axiosClient.jsx";

/**
 * PayoutsAPI — client for the country/rail-agnostic payout router.
 *
 * Two parallel surfaces coexist (migration per-rail, see PAYOUTS_V2_FE_INTEGRATION.md §Migration):
 *
 *   V1 (legacy, still live):
 *     GET    /payments/payout/routes
 *     GET    /payments/payout/routes/{rail_id}/entities
 *     GET    /payments/payout/transactions
 *     GET    /payments/payout/transactions/{id}
 *     POST   /payments/payout/transactions/{id}/cancel
 *     POST   /payments/simple_funds_out_*   (per-rail submit, discovered from V1 catalog)
 *
 *   V2 (new, catalog-driven, idempotency-keyed):
 *     GET    /v2/payouts/routes
 *     GET    /v2/payouts/routes/{rail_id}/entities
 *     POST   /v2/payouts
 *     GET    /v2/payouts
 *     GET    /v2/payouts/{id}
 *     POST   /v2/payouts/{id}/cancel
 */

/* RFC 4122 v4 — used as the Idempotency-Key for V2 submissions. The
   caller may also pass in a key to reuse on retries (that's the whole
   point of idempotency: same body + same key → cached response). */
function makeIdempotencyKey() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

class PayoutsAPI {
  // ---------- catalog ----------

  /** GET /payments/payout/routes — full enabled (country × rail) catalog. */
  getRoutes(token) {
    const headers = { headers: { Authorization: `Bearer ${token}` } };
    return axiosClient.get("/payments/payout/routes", headers);
  }

  /** GET /payments/payout/routes/{railId}/entities — banks for PSE, etc. */
  getRailEntities(token, railId) {
    const headers = { headers: { Authorization: `Bearer ${token}` } };
    return axiosClient.get(
      `/payments/payout/routes/${encodeURIComponent(railId)}/entities`,
      headers
    );
  }

  // ---------- transaction status ----------

  /** GET /payments/payout/transactions/{id} */
  getTransaction(token, transactionId) {
    const headers = { headers: { Authorization: `Bearer ${token}` } };
    return axiosClient.get(
      `/payments/payout/transactions/${encodeURIComponent(transactionId)}`,
      headers
    );
  }

  /** GET /payments/payout/transactions — filterable history. */
  listTransactions(token, { country, railId, status, from, to, limit, offset } = {}) {
    const headers = { headers: { Authorization: `Bearer ${token}` } };
    const params = [];
    if (country) params.push(`country=${encodeURIComponent(country)}`);
    if (railId)  params.push(`rail_id=${encodeURIComponent(railId)}`);
    if (status)  params.push(`status=${encodeURIComponent(status)}`);
    if (from)    params.push(`from=${encodeURIComponent(from)}`);
    if (to)      params.push(`to=${encodeURIComponent(to)}`);
    if (limit)   params.push(`limit=${limit}`);
    if (offset)  params.push(`offset=${offset}`);
    const qs = params.length ? `?${params.join("&")}` : "";
    return axiosClient.get(`/payments/payout/transactions${qs}`, headers);
  }

  // ---------- cancel ----------

  /** POST /payments/payout/transactions/{id}/cancel */
  cancelTransaction(token, transactionId) {
    const headers = { headers: { Authorization: `Bearer ${token}` } };
    return axiosClient.post(
      `/payments/payout/transactions/${encodeURIComponent(transactionId)}/cancel`,
      {},
      headers
    );
  }

  // ---------- submission (delegates to legacy endpoint discovered from catalog) ----------

  /**
   * Submit a payout. The caller passes the resolved `rail` object from the
   * routes catalog plus the field values keyed by `field.name`. We pick the
   * 2FA or non-2FA submit endpoint based on whether a `code` was provided.
   *
   * The shape posted to the legacy endpoint is the union of:
   *   - common fields: portfolio_name, currency, amount, note, code
   *   - rail-specific fields: whatever the catalog declared (bank_id,
   *     account_type, account_number, document_type, document_id,
   *     account_holder_name, breb_key, …)
   * Existing legacy DTOs (SimpleFundsOutBank, SimpleFundsOutCash,
   * SimpleFundsOutBreb) use these exact field names, so no mapping needed.
   */
  submitPayout(token, rail, { portfolio_name, currency, amount, note, code, fields }) {
    const headers = { headers: { Authorization: `Bearer ${token}` } };
    const endpoint = code ? rail.submit_endpoint_with_2fa : rail.submit_endpoint;
    const body = {
      portfolio_name,
      currency,
      amount: Number(amount),
      note: note || "",
      ...(fields || {}),
    };
    if (code) body.code = code;
    return axiosClient.post(endpoint, body, headers);
  }

  // ============================================================
  // V2 surface — /v2/payouts/* (PAYOUTS_V2_FE_INTEGRATION.md)
  // ============================================================
  //
  // Every mutating V2 call requires Idempotency-Key. The catalog is
  // shaped {success, routes:[{country_code, rails:[...]}]} and each
  // rail entry carries a `fields[]` schema the FE renders dynamically.
  // Amount is a string in V2 (precision-preserving for crypto / high-
  // decimal currencies).

  /** GET /v2/payouts/routes — V2 catalog, server-filtered by capability. */
  getRoutesV2(token, { country, currency } = {}) {
    const headers = { headers: { Authorization: `Bearer ${token}` } };
    const params = [];
    if (country)  params.push(`country=${encodeURIComponent(country)}`);
    if (currency) params.push(`currency=${encodeURIComponent(currency)}`);
    const qs = params.length ? `?${params.join("&")}` : "";
    return axiosClient.get(`/v2/payouts/routes${qs}`, headers);
  }

  /** GET /v2/payouts/routes/{rail_id}/entities — V2 dynamic options. */
  getRailEntitiesV2(token, railId) {
    const headers = { headers: { Authorization: `Bearer ${token}` } };
    return axiosClient.get(
      `/v2/payouts/routes/${encodeURIComponent(railId)}/entities`,
      headers,
    );
  }

  /**
   * POST /v2/payouts — submit. Body shape per spec §POST /v2/payouts:
   *   { country, currency, rail_id, method, amount (string),
   *     portfolio_id, recipient: { ...rail-specific fields }, note, code? }
   *
   * `idempotencyKey` MUST be reused across retries of the same submission;
   * pass null and we'll generate one. Same key + different body returns
   * 422 IDEMPOTENCY_KEY_CONFLICT, in which case the caller should
   * generate a fresh key and retry. We surface that error via the normal
   * axios reject path so the form layer can react.
   */
  submitPayoutV2(token, { country, currency, rail_id, method, amount, portfolio_id, recipient, note, code }, idempotencyKey) {
    const key = idempotencyKey || makeIdempotencyKey();
    const headers = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Idempotency-Key": key,
        "Content-Type": "application/json",
      },
    };
    const body = {
      country,
      currency,
      rail_id,
      method,
      amount: String(amount),        // string per spec — preserves precision
      portfolio_id,
      recipient: recipient || {},
      note: note || "",
    };
    if (code) body.code = code;
    return axiosClient.post("/v2/payouts", body, headers).then((resp) => {
      // attach the key used so callers can store it for retry
      if (resp && resp.data) resp.data._idempotencyKey = key;
      return resp;
    });
  }

  /** GET /v2/payouts/{id} — single payout incl. full event history. */
  getPayoutV2(token, payoutId) {
    const headers = { headers: { Authorization: `Bearer ${token}` } };
    return axiosClient.get(`/v2/payouts/${encodeURIComponent(payoutId)}`, headers);
  }

  /** GET /v2/payouts — paginated history; mirrors V1 listTransactions params. */
  listPayoutsV2(token, { country, railId, status, from, to, limit, offset } = {}) {
    const headers = { headers: { Authorization: `Bearer ${token}` } };
    const params = [];
    if (country) params.push(`country=${encodeURIComponent(country)}`);
    if (railId)  params.push(`rail_id=${encodeURIComponent(railId)}`);
    if (status)  params.push(`status=${encodeURIComponent(status)}`);
    if (from)    params.push(`from=${encodeURIComponent(from)}`);
    if (to)      params.push(`to=${encodeURIComponent(to)}`);
    if (limit)   params.push(`limit=${limit}`);
    if (offset)  params.push(`offset=${offset}`);
    const qs = params.length ? `?${params.join("&")}` : "";
    return axiosClient.get(`/v2/payouts${qs}`, headers);
  }

  /** POST /v2/payouts/{id}/cancel — idempotency-keyed. */
  cancelPayoutV2(token, payoutId, idempotencyKey) {
    const headers = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Idempotency-Key": idempotencyKey || makeIdempotencyKey(),
      },
    };
    return axiosClient.post(`/v2/payouts/${encodeURIComponent(payoutId)}/cancel`, {}, headers);
  }

  /** Re-export so callers can pre-generate / share keys across retries. */
  makeIdempotencyKey() { return makeIdempotencyKey(); }
}

export default new PayoutsAPI();
